import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, useResource, errMsg, notifyRefresh } from "@/lib/api";
import { findConflicts, suggestFreeSlots } from "@/lib/scheduling";
import { AlertTriangle, Sparkles } from "lucide-react";
import { PROCEDURES, STATUS, STATUS_KEYS, todayISO } from "@/lib/constants";

const blank = (initial = {}) => ({
  patient_id: "", date: todayISO(), time: "09:00", duration: 30, procedure: "Avaliação", notes: "", status: "agendado", ...initial,
});

export const AppointmentDialog = ({ open, onOpenChange, appointment, initial }) => {
  const api = useApi();
  const { data: patients } = useResource(open ? "/patients" : null);
  const [form, setForm] = useState(blank());
  const { data: appointments } = useResource(open && form.date ? `/appointments?start=${form.date}&end=${form.date}` : null);
  const [saving, setSaving] = useState(false);
  const dayAppointments = Array.isArray(appointments) ? appointments : (appointments?.items || []);
  const conflicts = findConflicts(dayAppointments, form, appointment?.id);
  const suggestions = suggestFreeSlots(dayAppointments, { ...form, id: appointment?.id });

  useEffect(() => {
    if (open) setForm(appointment ? blank(appointment) : blank(initial));
  }, [open, appointment, initial]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) return toast.error("Selecione o paciente");
    if (conflicts.length) return toast.error("Este horário já está ocupado. Escolha outro horário.");
    setSaving(true);
    const body = { patient_id: form.patient_id, date: form.date, time: form.time, duration: Number(form.duration),
      procedure: form.procedure, notes: form.notes, status: form.status };
    try {
      if (appointment) await api.put(`/appointments/${appointment.id}`, body);
      else await api.post("/appointments", body);
      toast.success(appointment ? "Consulta atualizada" : "Consulta agendada");
      notifyRefresh();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{appointment ? "Editar consulta" : "Novo agendamento"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label>Paciente</Label>
            <Select value={form.patient_id} onValueChange={set("patient_id")}>
              <SelectTrigger data-testid="appointment-patient-select"><SelectValue placeholder="Selecione o paciente" /></SelectTrigger>
              <SelectContent>
                {(patients || []).map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`appointment-patient-option-${p.id}`}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5">
              <Label>Data</Label>
              <Input type="date" value={form.date} onChange={set("date")} required data-testid="appointment-date-input" />
            </div>
            <div className="grid gap-1.5">
              <Label>Horário</Label>
              <Input type="time" value={form.time} onChange={set("time")} required data-testid="appointment-time-input" />
            </div>
            <div className="grid gap-1.5">
              <Label>Duração</Label>
              <Select value={String(form.duration)} onValueChange={set("duration")}>
                <SelectTrigger data-testid="appointment-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[15, 30, 45, 60, 90, 120].map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div aria-live="polite" className={`rounded-xl border p-3 ${conflicts.length ? "border-amber-200 bg-amber-50" : "border-sky-100 bg-sky-50/60"}`}>
            <div className="flex items-center gap-2 text-sm font-bold">
              {conflicts.length ? <AlertTriangle size={17} className="text-amber-700" /> : <Sparkles size={17} className="text-sky-700" />}
              <span className={conflicts.length ? "text-amber-900" : "text-sky-900"}>{conflicts.length ? "Possível conflito de horário" : "Assistente de horários"}</span>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">{conflicts.length ? `Já existe ${conflicts.length} consulta(s) sobreposta(s) nesse período. Escolha outro horário.` : "Sugestões baseadas nos agendamentos carregados para este dia. A disponibilidade definitiva deve ser validada no servidor."}</p>
            {suggestions.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{suggestions.map((time) => <button key={time} type="button" onClick={() => set("time")(time)} className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition hover:border-sky-400 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500 ${form.time === time ? "border-sky-600 bg-white text-sky-800" : "border-sky-200 bg-white/70 text-slate-700"}`}>{time}</button>)}</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Procedimento</Label>
              <Select value={form.procedure} onValueChange={set("procedure")}>
                <SelectTrigger data-testid="appointment-procedure-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...new Set([form.procedure, ...PROCEDURES])].filter(Boolean).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="appointment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_KEYS.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Observações</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="appointment-notes-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving || conflicts.length > 0} className="bg-sky-600 hover:bg-sky-700" data-testid="appointment-save-btn">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
